import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import { ReactNode, memo, useState } from 'react';
import { getColor } from '../assets/theme';
import {
  PrivacyTipOutlined,
  SvgIconComponent,
  InfoOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@store/index';
import { selectMaintenanceMode } from '@store/selector/api.selector';

/**
 * A reusable information dialog component.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {string} [props.title] - The title of the dialog
 * @param {string|ReactNode} [props.desc] - The description or content of the dialog
 * @param {SvgIconComponent} [props.icon] - The icon to display in the dialog
 * @param {boolean} [props.hideIcon=false] - Whether to hide the icon
 * @param {() => void} [props.onClose] - Function to call when the dialog is closed
 * @param {string} [props.iconColor] - The color of the icon
 * @returns {JSX.Element} The InformationDialog component
 */
export const InformationDialog = memo(
  ({
    open,
    title,
    desc,
    icon,
    hideIcon = false,
    onClose,
    iconColor,
    additionalBtn,
    btnProps,
  }: {
    open: boolean;
    title?: string;
    desc?: string | ReactNode;
    icon?: SvgIconComponent;
    hideIcon?: boolean;
    onClose?: () => void;
    iconColor?: string;
    additionalBtn?: { title: string; onClick: () => void };
    btnProps?: ButtonProps;
  }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const isMaintenance = useAppSelector(selectMaintenanceMode);

    const navigateToLogin = () => {
      if (!isMaintenance) return;

      if (location.pathname !== '/login') {
        navigate('/login');
      }
    };

    return (
      <Dialog
        open={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            additionalBtn?.onClick() ?? onClose?.();
          }
        }}
      >
        <Stack
          sx={{
            bgcolor: getColor().white,
            borderRadius: '4px',
            p: '36px 34px 24px 34px',
            justifyContent: 'flex-start',
            minWidth: { xs: '100%', md: '350px' },
          }}
        >
          {!hideIcon && (
            <Box sx={{ width: '100%' }}>
              <SvgIcon
                component={icon ? icon : PrivacyTipOutlined}
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#F7F7FB',
                  p: '12px',
                  borderRadius: '100%',
                  color: iconColor || getColor().primary,
                }}
              />
            </Box>
          )}
          <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
            {title || 'Confirmation'}
          </DialogTitle>
          <DialogContent sx={{ p: '8px 0!important', maxHeight: '400px' }}>
            {!desc && (
              <Typography sx={{ pb: '24px' }}>
                Are you sure you want to proceed? This action cannot be undone.
              </Typography>
            )}
            {typeof desc == 'string' ? (
              <Typography sx={{ pb: '24px' }}>
                <div dangerouslySetInnerHTML={{ __html: desc }} />
              </Typography>
            ) : (
              <Box sx={{ pb: '16px' }}> {desc}</Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: '0 0 8px 0' }}>
            <Stack direction='row' gap='8px'>
              <Button
                variant='text'
                onClick={() => {
                  onClose && onClose();
                  navigateToLogin();
                }}
                {...btnProps}
              >
                OK
              </Button>
              {additionalBtn && (
                <Button variant='outlined' onClick={additionalBtn.onClick}>
                  {additionalBtn.title}
                </Button>
              )}
            </Stack>
          </DialogActions>
        </Stack>
      </Dialog>
    );
  },
);

/**
 * Custom hook for managing an information dialog.
 *
 * @returns {Object} An object containing dialog-related functions and components
 * @property {React.FC} DialogInformation - A memo-ized dialog component
 * @property {Function} onShowInfoDialog - Function to show the information dialog
 * @property {React.FC} DialogInformationV2 - The InformationDialog component
 */
const useInformationDialog = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { white, primary } = getColor();
  const [dialogInfoDesc, setDialogInfoDesc] = useState('');
  const [dialogInfoTitle, setDialogInfoTitle] = useState('');

  const [dialogOnOk, setDialogOnOk] = useState<(() => void) | undefined>();

  /**
   * Shows the information dialog with the given title, description, and onOk callback.
   *
   * @param {Object} params - The parameters for showing the dialog
   * @param {string} [params.title] - The title of the dialog
   * @param {string} [params.desc] - The description or content of the dialog
   * @param {() => void} [params.onOk] - Function to call when OK is clicked
   */
  const onShowInfoDialog = ({
    title,
    desc,
    onOk,
  }: {
    title?: string;
    desc?: string;
    onOk?: () => void;
  }) => {
    setDialogInfoTitle(title || 'Information');
    setDialogInfoDesc(desc || 'Please verify before proceeding.');
    setDialogOnOk(() => onOk);
    setShowDialog(true);
  };

  /**
   * Handles the keydown event for the dialog, closing it on Enter key press.
   *
   * @param {React.KeyboardEvent<HTMLDivElement>} event - The keydown event
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleDialogClose();
      setShowDialog(false);
      event.preventDefault(); // Prevent default action to avoid any side effects
    }
  };

  /**
   * Closes the dialog and calls the onOk callback if it exists.
   */
  const handleDialogClose = () => {
    setShowDialog(false);

    if (dialogOnOk) {
      dialogOnOk();
    }
  };

  /**
   * A memo-ized dialog component.
   *
   * @returns {JSX.Element} The DialogInformation component
   */
  const DialogInformation = memo(() => {
    return (
      <Dialog open={showDialog} onKeyDown={handleKeyDown}>
        <Stack
          sx={{
            bgcolor: white,
            borderRadius: '4px',
            p: '36px 34px 24px 34px',
            justifyContent: 'flex-start',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <IconButton
              sx={{
                color: primary,
                backgroundColor: '#f7f7fb',
                width: '50px',
                height: '50px',
              }}
              // onClick={() => setShowDialog(false)}
            >
              <InfoOutlined />
            </IconButton>
          </Box>
          <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
            {dialogInfoTitle}
          </DialogTitle>
          <DialogContent sx={{ p: '16px 0!important' }}>
            <Typography
              sx={{ pb: '24px', minWidth: '350px', whiteSpace: 'pre-wrap' }}
            >
              {dialogInfoDesc}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: '0 0 8px 0' }}>
            <Stack direction='row' gap='8px'>
              <Button variant='outlined' onClick={handleDialogClose}>
                OK
              </Button>
            </Stack>
          </DialogActions>
        </Stack>
      </Dialog>
    );
  });

  return {
    DialogInformation,
    onShowInfoDialog,
    DialogInformationV2: InformationDialog,
  };
};

export default useInformationDialog;
